import { HttpMethods, makeRequest, type MakeRequest } from '../helpers/http.helper';
import {
  CreatePreSignedUrlRequest,
  CreatePreSignedUrlResponse,
  FileExtension,
  FileType,
} from '../types/upload.type';
import { isDemoMode } from '../demo/core';
import { DEMO_UPLOAD_SCHEME, registerDemoUpload } from '../demo/data/uploads';

function unwrapPresignedResponse(raw: unknown): CreatePreSignedUrlResponse {
  if (
    raw &&
    typeof raw === 'object' &&
    'result' in raw &&
    (raw as { result: unknown }).result &&
    typeof (raw as { result: unknown }).result === 'object'
  ) {
    return (raw as { result: CreatePreSignedUrlResponse }).result;
  }
  return raw as CreatePreSignedUrlResponse;
}

export const generatePresignedUrl = (
  data: CreatePreSignedUrlRequest,
  fileType: FileType,
  fileExtension: FileExtension,
  extraHeaders?: Record<string, string>
): Promise<CreatePreSignedUrlResponse> => {
  return makeRequest({
    url: '/upload',
    method: HttpMethods.POST,
    data,
    params: {
      file_type: fileType,
      file_extension: fileExtension,
    },
    headers: extraHeaders as MakeRequest['headers'],
  }).then(unwrapPresignedResponse);
};

export const uploadFileToPresignedUrl = async (
  file: File,
  presignedUrl: string,
  contentType?: string
): Promise<void> => {
  // Demo: the fake API hands out demo:// URLs; keep the file locally instead of uploading.
  if (isDemoMode() || presignedUrl.startsWith(DEMO_UPLOAD_SCHEME)) {
    registerDemoUpload(presignedUrl, file);
    return;
  }
  const response = await fetch(presignedUrl, {
    method: HttpMethods.PUT,
    body: file,
    headers: contentType ? { 'Content-Type': contentType } : undefined,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Upload failed: ${response.status} ${response.statusText} - ${errorText}`
    );
  }
};
